import { useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLanguage } from "../i18n/LanguageContext";

/**
 * Returns a callback that yields a non-expired Auth0 ID token.
 *
 * getIdTokenClaims() is a pure cache read — it never renews. The Auth0 session
 * (localStorage) outlives the ID token's own lifetime, so the cached token is
 * routinely expired even though the user is still signed in. cacheMode 'off'
 * forces a network renewal, which rewrites the cached id_token; without it the
 * API rejects the call with "jwt expired".
 */
export default function useFreshIdToken() {
    const { getIdTokenClaims, getAccessTokenSilently } = useAuth0();
    const { t } = useLanguage();

    return useCallback(async () => {
        let claims = await getIdTokenClaims();
        const expiresAt = claims?.exp ? claims.exp * 1000 : 0;

        if (expiresAt - Date.now() > 60000) {
            return claims?.__raw;
        }

        try {
            await getAccessTokenSilently({ cacheMode: "off" });
        } catch (e) {
            console.error("Silent token renewal failed", e);
            throw new Error(t("common.sessionExpired"));
        }

        claims = await getIdTokenClaims();

        return claims?.__raw;
    }, [getIdTokenClaims, getAccessTokenSilently, t]);
}
